import { CartDto } from '@cineamo/api/models/cart/CartDto.types';
import { CartConcessionDto } from '@cineamo/api/models/cart-concession/CartConcessionDto.types';

import { ApiError } from '@cineamo/api/types/ApiErrorResponse.types';

import { ReasonCode } from '@cineamo/api/constants/error-code-constants';

/**
 * Gross cents per unit including the online ticketing fee. Mirrors `ProductPriceCalculator`: the fee is
 * feePercent of the gross catalog price, rounded down so it never exceeds that percent. The unit result is
 * multiplied by the quantity afterwards, so the lines add up to the cart's `priceTotal`.
 */
export function withOnlineTicketingFee(
    grossCents: number,
    feePercent: NonNullable<CartDto['onlineTicketingFee']>
): number {
    return grossCents + Math.floor((grossCents * feePercent) / 100);
}

export function concessionLineTotal(
    concession: Pick<CartConcessionDto, 'unitPrice' | 'quantity'>,
    feePercent: NonNullable<CartDto['onlineTicketingFee']>
): number {
    return withOnlineTicketingFee(concession.unitPrice, feePercent) * concession.quantity;
}

/** Translation key (below `tickets.error`) for a failed `PUT /carts/{uuid}/concessions`. */
export function concessionErrorKey(error: ApiError): string {
    if (error.status === 403) {
        return error.data?.reason_code === ReasonCode.CART_IS_PAID
            ? 'concession-cart-paid'
            : 'concession-sale-disabled';
    }
    const productErrors = (error.data?.parameters as Record<string, Record<string, string>> | undefined)
        ?.cinemaProductId;
    if (productErrors?.isInactive) return 'concession-product-inactive';
    if (productErrors?.notSameCinema) return 'concession-product-other-cinema';
    if (productErrors?.isNotLinked) return 'concession-product-not-linked';
    if (productErrors?.isUnknown) return 'concession-product-unknown';
    return 'something-went-wrong';
}

/** The cart validator's `concessions_without_tickets` reason, wherever it sits in the error's `parameters`. */
export function isConcessionsWithoutTicketsError(error: { data?: { parameters?: unknown } | null }): boolean {
    return JSON.stringify(error.data?.parameters ?? {}).includes('concessions_without_tickets');
}
